import React, { useState, useEffect } from 'react';
import dashboardService from '../services/dashboardService';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import {
  FaShieldAlt, FaClock, FaCalendarCheck,
  FaMoneyBillWave, FaHourglassHalf, FaExclamationCircle,
  FaSpinner, FaSync, FaChartBar, FaCalendarAlt, FaClipboardList,
  FaBell, FaPercentage
} from 'react-icons/fa';
import '../styles/MaintenanceDashboard.css';

const STATUS_COLORS = {
  pending: '#f39c12',
  accepted: '#3498db',
  in_progress: '#9b59b6',
  resolved: '#2ecc71',
  closed: '#1abc9c',
  rejected: '#e74c3c'
};
const SEVERITY_COLORS = { high: '#e74c3c', medium: '#f39c12', low: '#2ecc71' };

const PreventiveDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getPreventiveStats();
      if (response.success) {
        setData(response.data);
      }
    } catch (err) {
      setError('Failed to load preventive dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="maint-dash-loading">
        <FaSpinner className="spin" />
        <p>Loading Preventive Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="maint-dash-error">
        <FaExclamationCircle />
        <p>{error}</p>
        <button onClick={fetchData}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, severity, issuesByDepartment, issuesByMonth, scheduleData, maintenanceLogs, recentIssues } = data;

  const FREQUENCY_LABELS = {
    weekly: 'Weekly',
    biweekly: 'Bi-Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    semi_annual: 'Semi-Annual',
    yearly: 'Yearly',
    custom: 'Custom'
  };

  const getFrequencyLabel = (freq) => FREQUENCY_LABELS[freq] || freq || 'Not Set';
  const getFrequencyClass = (freq) => {
    const map = { weekly: 'freq-weekly', biweekly: 'freq-biweekly', monthly: 'freq-monthly', quarterly: 'freq-quarterly', semi_annual: 'freq-semi', yearly: 'freq-yearly' };
    return map[freq] || 'freq-custom';
  };

  const statusPieData = [
    { name: 'Pending', value: summary.pendingIssues, color: STATUS_COLORS.pending },
    { name: 'Accepted', value: summary.acceptedIssues, color: STATUS_COLORS.accepted },
    { name: 'In Progress', value: summary.inProgressIssues, color: STATUS_COLORS.in_progress },
    { name: 'Resolved', value: summary.resolvedIssues, color: STATUS_COLORS.resolved },
    { name: 'Closed', value: summary.closedIssues, color: STATUS_COLORS.closed },
    { name: 'Rejected', value: summary.rejectedIssues, color: STATUS_COLORS.rejected }
  ].filter(d => d.value > 0);

  const severityPieData = [
    { name: 'High', value: severity.high, color: SEVERITY_COLORS.high },
    { name: 'Medium', value: severity.medium, color: SEVERITY_COLORS.medium },
    { name: 'Low', value: severity.low, color: SEVERITY_COLORS.low }
  ].filter(d => d.value > 0);

  const activeIssues = summary.pendingIssues + summary.acceptedIssues + summary.inProgressIssues;

  return (
    <div className="maint-dashboard preventive-theme">
      {/* Header */}
      <div className="maint-dash-header">
        <div className="maint-dash-title">
          <FaShieldAlt className="header-icon preventive-icon" />
          <div>
            <h1>Preventive Maintenance</h1>
            <p>Scheduled maintenance tracking and compliance monitoring</p>
          </div>
        </div>
        <button className="btn-refresh" onClick={fetchData}>
          <FaSync /> Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="maint-dash-tabs">
        <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
          <FaChartBar /> Overview
        </button>
        <button className={activeTab === 'schedule' ? 'active' : ''} onClick={() => setActiveTab('schedule')}>
          <FaCalendarAlt /> Maintenance Schedule
        </button>
        <button className={activeTab === 'logs' ? 'active' : ''} onClick={() => setActiveTab('logs')}>
          <FaClipboardList /> Maintenance Logs
        </button>
        <button className={activeTab === 'issues' ? 'active' : ''} onClick={() => setActiveTab('issues')}>
          <FaClipboardList /> Recent Issues
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card info">
              <div className="kpi-icon"><FaShieldAlt /></div>
              <div className="kpi-content">
                <span className="kpi-value">{summary.totalIssues}</span>
                <span className="kpi-label">Total Preventive Issues</span>
              </div>
            </div>
            <div className="kpi-card warning">
              <div className="kpi-icon"><FaClock /></div>
              <div className="kpi-content">
                <span className="kpi-value">{activeIssues}</span>
                <span className="kpi-label">Active Issues</span>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="kpi-icon"><FaPercentage /></div>
              <div className="kpi-content">
                <span className="kpi-value">{summary.complianceRate}%</span>
                <span className="kpi-label">Compliance Rate</span>
              </div>
            </div>
            <div className="kpi-card purple">
              <div className="kpi-icon"><FaHourglassHalf /></div>
              <div className="kpi-content">
                <span className="kpi-value">{summary.avgResolutionHours}h</span>
                <span className="kpi-label">Avg Resolution Time</span>
              </div>
            </div>
            <div className="kpi-card teal">
              <div className="kpi-icon"><FaMoneyBillWave /></div>
              <div className="kpi-content">
                <span className="kpi-value">₹{summary.totalCost.toLocaleString()}</span>
                <span className="kpi-label">Total PM Cost</span>
              </div>
            </div>
            <div className="kpi-card danger">
              <div className="kpi-icon"><FaExclamationCircle /></div>
              <div className="kpi-content">
                <span className="kpi-value">{summary.overdueMaintenanceCount}</span>
                <span className="kpi-label">Overdue Assets</span>
              </div>
            </div>
          </div>

          {/* Schedule Summary Bar */}
          <div className="log-summary-bar preventive-bar">
            <div className="log-summary-item">
              <span className="log-dot total"></span>
              <span>Assets with Cycles: <strong>{summary.totalAssetsWithCycles}</strong></span>
            </div>
            <div className="log-summary-item">
              <span className="log-dot warning-dot"></span>
              <span>Due Soon (7 days): <strong>{summary.dueSoonCount}</strong></span>
            </div>
            <div className="log-summary-item">
              <span className="log-dot upcoming"></span>
              <span>Upcoming (30 days): <strong>{summary.upcomingMaintenanceCount}</strong></span>
            </div>
            <div className="log-summary-item">
              <span className="log-dot overdue"></span>
              <span>Overdue: <strong>{summary.overdueMaintenanceCount}</strong></span>
            </div>
            <div className="log-summary-item">
              <span className="log-dot completed"></span>
              <span>Completed Logs: <strong>{summary.completedLogs}</strong></span>
            </div>
          </div>

          {/* Charts Row */}
          <div className="charts-row">
            <div className="chart-card">
              <h3>Issue Status Distribution</h3>
              {statusPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={statusPieData} cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {statusPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-data">No issue data yet</p>}
            </div>

            <div className="chart-card">
              <h3>Severity Breakdown</h3>
              {severityPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={severityPieData} cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {severityPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-data">No severity data yet</p>}
            </div>

            <div className="chart-card">
              <h3>Issues by Department</h3>
              {issuesByDepartment.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={issuesByDepartment}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#2ecc71" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">No department data yet</p>}
            </div>
          </div>

          {/* Trend Chart */}
          <div className="chart-card full-width">
            <h3>Preventive Maintenance Trend (Last 6 Months)</h3>
            {issuesByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={issuesByMonth}>
                  <defs>
                    <linearGradient id="preventiveGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2ecc71" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2ecc71" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#2ecc71" fill="url(#preventiveGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="no-data">No trend data yet</p>}
          </div>
        </>
      )}

      {/* Maintenance Schedule Tab */}
      {activeTab === 'schedule' && (
        <div className="table-card">
          <h3>
            <FaCalendarCheck style={{ marginRight: 8 }} />
            Asset Maintenance Schedules
          </h3>
          <p className="schedule-subtitle">All assets with configured maintenance cycles and their scheduling details</p>
          {scheduleData && scheduleData.length > 0 ? (
            <>
              {/* Schedule Summary Chips */}
              <div className="schedule-chips">
                <span className="sched-chip total">{scheduleData.length} Total Assets</span>
                <span className="sched-chip overdue">{scheduleData.filter(s => s.isOverdue).length} Overdue</span>
                <span className="sched-chip due-soon">{scheduleData.filter(s => s.isDueSoon).length} Due Soon</span>
                <span className="sched-chip scheduled">{scheduleData.filter(s => s.isScheduled).length} Scheduled</span>
                <span className="sched-chip on-track">{scheduleData.filter(s => !s.isOverdue && !s.isDueSoon && !s.isScheduled).length} On Track</span>
              </div>
              <div className="table-scroll">
                <table className="maint-table schedule-table">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Serial No.</th>
                      <th>Category</th>
                      <th>Department</th>
                      <th>Frequency</th>
                      <th>Cycle</th>
                      <th>Last Maintenance</th>
                      <th>Next Due</th>
                      <th>Condition</th>
                      <th>Assigned To</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleData
                      .sort((a, b) => {
                        if (a.isOverdue && !b.isOverdue) return -1;
                        if (!a.isOverdue && b.isOverdue) return 1;
                        if (a.isDueSoon && !b.isDueSoon) return -1;
                        if (!a.isDueSoon && b.isDueSoon) return 1;
                        if (a.isScheduled && !b.isScheduled) return -1;
                        if (!a.isScheduled && b.isScheduled) return 1;
                        return 0;
                      })
                      .map((item) => {
                        const getCycleLabel = () => {
                          if (item.cycleYears && item.cycleMonths) return `${item.cycleYears}y ${item.cycleMonths}m`;
                          if (item.cycleYears) return `${item.cycleYears} year${item.cycleYears > 1 ? 's' : ''}`;
                          if (item.cycleMonths) return `${item.cycleMonths} month${item.cycleMonths > 1 ? 's' : ''}`;
                          return item.cycleDays ? `${item.cycleDays} days` : '—';
                        };
                        return (
                          <tr key={item._id} className={item.isOverdue ? 'row-overdue' : item.isDueSoon ? 'row-due-soon' : item.isScheduled ? 'row-scheduled' : ''}>
                            <td className="td-asset">{item.assetName}</td>
                            <td className="td-serial">{item.serialNumber || '—'}</td>
                            <td><span className="category-tag">{item.category || '—'}</span></td>
                            <td>{item.department}</td>
                            <td>
                              <span className={`freq-badge ${getFrequencyClass(item.frequency)}`}>
                                {getFrequencyLabel(item.frequency)}
                              </span>
                            </td>
                            <td className="td-cycle">{getCycleLabel()}</td>
                            <td>
                              <div className="date-cell">
                                {item.lastMaintenance ? (
                                  <>
                                    <span>{new Date(item.lastMaintenance).toLocaleDateString()}</span>
                                    <span className="date-ago">
                                      {Math.floor((new Date() - new Date(item.lastMaintenance)) / (1000 * 60 * 60 * 24))} days ago
                                    </span>
                                  </>
                                ) : (
                                  <span className="never-done">Never done</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="date-cell">
                                {item.nextDue ? (
                                  <>
                                    <span>{new Date(item.nextDue).toLocaleDateString()}</span>
                                    {item.isOverdue && <FaBell className="overdue-bell" title="Overdue!" />}
                                    {item.isDueSoon && !item.isOverdue && <FaClock className="due-soon-icon" title="Due soon" />}
                                    <span className={`date-ago ${item.isOverdue ? 'text-danger' : item.isDueSoon ? 'text-warning' : ''}`}>
                                      {item.isOverdue
                                        ? `${Math.floor((new Date() - new Date(item.nextDue)) / (1000 * 60 * 60 * 24))} days overdue`
                                        : `in ${Math.ceil((new Date(item.nextDue) - new Date()) / (1000 * 60 * 60 * 24))} days`
                                      }
                                    </span>
                                  </>
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>Not set</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`badge condition-${item.condition || 'good'}`}>
                                {item.condition || '—'}
                              </span>
                            </td>
                            <td>
                              {item.isScheduled ? (
                                <span className="scheduled-tech">{item.scheduledTo || 'Unassigned'}</span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>—</span>
                              )}
                            </td>
                            <td>
                              {item.isScheduled ? (
                                <span className="badge status-scheduled">
                                  {item.scheduledStatus === 'in_progress' ? 'In Progress' : 'Scheduled'}
                                </span>
                              ) : item.isOverdue ? (
                                <span className="badge status-overdue">Overdue</span>
                              ) : item.isDueSoon ? (
                                <span className="badge status-due-soon">Due Soon</span>
                              ) : (
                                <span className="badge status-on-track">On Track</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          ) : <p className="no-data">No assets with maintenance cycles configured</p>}
        </div>
      )}

      {/* Maintenance Logs Tab (Preventive) */}
      {activeTab === 'logs' && (
        <div className="table-card">
          <h3>
            <FaClipboardList style={{ marginRight: 8 }} />
            Preventive Maintenance Logs
          </h3>
          <p className="schedule-subtitle">All maintenance logs for preventive category — scheduled work, completion records, and assignments</p>
          {maintenanceLogs && maintenanceLogs.length > 0 ? (
            <>
              {/* Log Summary Chips */}
              <div className="schedule-chips">
                <span className="sched-chip total">{maintenanceLogs.length} Total Logs</span>
                <span className="sched-chip pending-chip">{maintenanceLogs.filter(l => l.status === 'pending').length} Pending</span>
                <span className="sched-chip progress-chip">{maintenanceLogs.filter(l => l.status === 'in_progress').length} In Progress</span>
                <span className="sched-chip completed-chip">{maintenanceLogs.filter(l => l.status === 'completed').length} Completed</span>
                <span className="sched-chip rejected-chip">{maintenanceLogs.filter(l => l.status === 'rejected').length} Rejected</span>
              </div>
              <div className="table-scroll">
                <table className="maint-table">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Serial No.</th>
                      <th>Department</th>
                      <th>Priority</th>
                      <th>Assigned To</th>
                      <th>Reported Date</th>
                      <th>Expected Completion</th>
                      <th>Completed Date</th>
                      <th>Cost</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintenanceLogs.map((log) => (
                      <tr key={log._id} className={log.status === 'completed' ? 'row-completed' : log.status === 'pending' ? 'row-pending-log' : ''}>
                        <td className="td-asset">{log.asset}</td>
                        <td className="td-serial">{log.serialNumber || '—'}</td>
                        <td>{log.department}</td>
                        <td><span className={`badge severity-${log.priorityLevel}`}>{log.priorityLevel}</span></td>
                        <td>
                          {log.sentTo !== 'Unassigned' ? (
                            <span className="scheduled-tech">{log.sentTo}</span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>Unassigned</span>
                          )}
                        </td>
                        <td>{log.reportedDate ? new Date(log.reportedDate).toLocaleDateString() : '—'}</td>
                        <td>
                          {log.expectedCompletionDate ? (
                            <span>
                              {new Date(log.expectedCompletionDate).toLocaleDateString()}
                              {log.status !== 'completed' && new Date(log.expectedCompletionDate) < new Date() && (
                                <FaBell className="overdue-bell" title="Overdue!" />
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          {log.completedDate ? (
                            <span className="text-success">{new Date(log.completedDate).toLocaleDateString()}</span>
                          ) : '—'}
                        </td>
                        <td>{log.cost > 0 ? `₹${log.cost.toLocaleString()}` : '—'}</td>
                        <td><span className={`badge status-${log.status}`}>{log.status.replace('_', ' ')}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : <p className="no-data">No preventive maintenance logs found</p>}
        </div>
      )}

      {/* Recent Issues Tab */}
      {activeTab === 'issues' && (
        <div className="table-card">
          <h3>Recent Preventive Maintenance Issues</h3>
          {recentIssues.length > 0 ? (
            <div className="table-scroll">
              <table className="maint-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Department</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Reported By</th>
                    <th>Technician</th>
                    <th>Reported At</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {recentIssues.map((issue) => (
                    <tr key={issue._id}>
                      <td className="td-asset">{issue.asset}</td>
                      <td>{issue.department}</td>
                      <td><span className={`badge severity-${issue.severity}`}>{issue.severity}</span></td>
                      <td><span className={`badge status-${issue.status}`}>{issue.status.replace('_', ' ')}</span></td>
                      <td>{issue.reportedBy}</td>
                      <td>{issue.technician}</td>
                      <td>{new Date(issue.reportedAt).toLocaleDateString()}</td>
                      <td className="td-desc">{issue.description?.substring(0, 60)}{issue.description?.length > 60 ? '...' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="no-data">No preventive issues found</p>}
        </div>
      )}
    </div>
  );
};

export default PreventiveDashboard;
